/**
 * BootScene - Initial boot and service initialization
 */

import Phaser from 'phaser';

import { ServiceLocator, Services } from '../utils/ServiceLocator';
// MMO managers
import { MMOStateManager } from '../managers/MMOStateManager';
import { MMONetworkManager } from '../managers/MMONetworkManager';
import { MMOProgressionManager } from '../managers/MMOProgressionManager';
import { MMOMonetizationManager } from '../managers/MMOMonetizationManager';
import { WalletManager } from '../managers/WalletManager';
import { PaymentFlowManager } from '../managers/PaymentFlowManager';
import { MemoryManager } from '../managers/MemoryManager';
import { AudioManager } from '../managers/AudioManager';
import { I18n } from '../i18n/I18nService';

export class BootScene extends Phaser.Scene {
  constructor() {
    super({ key: 'BootScene' });
  }

  init(): void {
    console.log('BootScene: Initializing...');
  }

  preload(): void {
    // Load minimal assets needed for preload screen
    // (loading bar graphics, logo, etc.)
  }

  create(): void {
    // Initialize core services
    this.initializeServices();

    // Proceed to preload scene.
    // Deferred to next frame via events.once('update') to ensure the scene
    // manager's update loop is running before we queue the transition.
    // Direct scene.start() in create() silently fails in Phaser 3.90.0
    // because the scene manager hasn't begun processing pending operations yet.
    console.log('BootScene: Starting PreloadScene...');
    this.events.once('update', () => {
      this.scene.start('PreloadScene');
    });
  }

  private initializeServices(): void {
    console.log('BootScene: Registering services...');

    // I18n (must be first — other services may need translated strings)
    I18n.init();
    ServiceLocator.register(Services.I18N, I18n);

    // MMO managers
    const mmoState = new MMOStateManager();
    ServiceLocator.register(Services.MMO_STATE, mmoState);

    const mmoNetwork = new MMONetworkManager();
    ServiceLocator.register(Services.MMO_NETWORK, mmoNetwork);

    const mmoProgression = new MMOProgressionManager(this);
    ServiceLocator.register(Services.MMO_PROGRESSION, mmoProgression);

    const mmoMonetization = new MMOMonetizationManager(() => mmoNetwork.getSessionToken());
    ServiceLocator.register(Services.MMO_MONETIZATION, mmoMonetization);

    const wallet = new WalletManager();
    ServiceLocator.register(Services.WALLET, wallet);

    const paymentFlow = new PaymentFlowManager(() => mmoNetwork.getSessionToken());
    ServiceLocator.register(Services.PAYMENT_FLOW, paymentFlow);

    const memoryManager = new MemoryManager();
    ServiceLocator.register(Services.MEMORY, memoryManager);
    memoryManager.initialize(this.game);

    const audioManager = new AudioManager();
    ServiceLocator.register(Services.AUDIO, audioManager);

    ServiceLocator.markInitialized();
    console.log('BootScene: Services registered:', ServiceLocator.getRegisteredServices());
  }
}
